/**
 * Platform Admin — 模型能力 (Model Capabilities).
 *
 * VISTOERA_MODEL_CAPABILITY_REGISTRY_OFFICIAL_SYNC_ADMIN_OVERRIDE_
 * EFFECTIVE_RESOLUTION_AND_FUNCTION_NODE_MODEL_SELECTION_P0_V1 (task
 * spec §5/§6/§7/§31/§34) — canonical Model Capability Registry list +
 * detail. Category tabs (§9) are browse-only; the real Input/Output/
 * Parameter/Combination atomic capabilities always come from Effective
 * Capability, never from `primary_category`.
 *
 * List page loads everything in ONE call (`platformListModelCapabilities`
 * — server-side summary, task spec §41 no-N+1); the detail drawer loads
 * ONE more call (`platformGetModelCapabilityDetail`) when opened. Saving
 * an override never does `window.location.reload()` (task spec §35) —
 * it re-fetches only the detail + patches the matching list row in
 * local state.
 */
import { CloudSyncOutlined, InfoCircleOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Drawer, Empty, Input, Modal, Select, Table, Tabs, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  platformActivateModelCapabilitySnapshot,
  platformGetModelCapabilityDetail,
  platformListCredentialProviders,
  platformListModelCapabilities,
  platformRemoveModelCapabilityOverride,
  platformSetModelCapabilityOverride,
  platformSyncAllModelCapabilities,
  platformSyncModelCapability,
} from '../api'
import type {
  CapabilityOverrideType,
  CapabilityStatus,
  ModelCapabilityDetail,
  ModelCapabilitySummaryRow,
  PrimaryCategory,
  ProviderDefinitionRow,
} from '../types'
import { SectionCard, formatDateTime } from './adminShared'
import { platformColors } from './theme'

function errMsg(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { code?: string; detail?: { code?: string; message?: string } } } })
    ?.response?.data
  const code = data?.code || data?.detail?.code
  const detailMessage = data?.detail?.message
  const raw = (err as { message?: string })?.message
  return code ? `${detailMessage || raw || fallback} (${code})` : detailMessage || raw || fallback
}

const CATEGORY_TABS: { key: 'ALL' | PrimaryCategory; label: string }[] = [
  { key: 'ALL', label: '全部' },
  { key: 'TEXT', label: '文本/对话' },
  { key: 'IMAGE', label: '图像生成' },
  { key: 'VIDEO', label: '视频生成' },
  { key: 'AUDIO', label: '语音/音频' },
  { key: 'MULTIMODAL', label: '综合/多模态' },
]

const STATUS_COLOR: Record<CapabilityStatus, string> = {
  NOT_CONFIGURED: 'default',
  PENDING_REVIEW: 'warning',
  VERIFIED: 'success',
  TESTING: 'processing',
  DISABLED: 'error',
}

const STATUS_LABEL: Record<CapabilityStatus, string> = {
  NOT_CONFIGURED: '未配置',
  PENDING_REVIEW: '待审核',
  VERIFIED: '已验证',
  TESTING: '测试中',
  DISABLED: '已停用',
}

const MODALITY_LABEL: Record<string, string> = {
  TEXT: '文本 (Text)',
  IMAGE: '图片 (Image)',
  VIDEO: '视频 (Video)',
  AUDIO: '音频 (Audio)',
  FILE: '文件 (File)',
  MULTIPLE_IMAGES: '多图输入 (Multiple Images)',
  MULTIPLE_FILES: '多文件输入 (Multiple Files)',
  STRUCTURED_DATA: '结构化数据 (JSON)',
}

function ModalityTags({ modalities }: { modalities: string[] }) {
  if (modalities.length === 0) {
    return <span style={{ color: platformColors.mutedText, fontSize: 12 }}>—</span>
  }
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {modalities.map((m) => (
        <Tooltip key={m} title={MODALITY_LABEL[m] || m}>
          <Tag style={{ margin: 0, fontSize: 11 }} aria-label={MODALITY_LABEL[m] || m}>
            {MODALITY_LABEL[m]?.split(' ')[0] || m}
          </Tag>
        </Tooltip>
      ))}
    </div>
  )
}

function ModalityChecklist({
  allKnown,
  active,
}: {
  allKnown: string[]
  active: string[]
}) {
  const activeSet = useMemo(() => new Set(active), [active])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {allKnown.map((m) => {
        const on = activeSet.has(m)
        return (
          <div key={m} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>{MODALITY_LABEL[m] || m}</span>
            <span style={{ color: on ? platformColors.success : platformColors.mutedText }}>{on ? '✓' : '✕'}</span>
          </div>
        )
      })}
    </div>
  )
}

const KNOWN_INPUT_MODALITIES = ['TEXT', 'IMAGE', 'MULTIPLE_IMAGES', 'VIDEO', 'AUDIO', 'FILE', 'STRUCTURED_DATA']
const KNOWN_OUTPUT_MODALITIES = ['TEXT', 'IMAGE', 'VIDEO', 'AUDIO', 'FILE', 'STRUCTURED_DATA']

function OverrideEditorModal({
  open,
  providerModelId,
  onClose,
  onSaved,
}: {
  open: boolean
  providerModelId: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const [capabilityKey, setCapabilityKey] = useState('')
  const [overrideType, setOverrideType] = useState<CapabilityOverrideType>('FORCE_ENABLE')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setCapabilityKey('')
      setOverrideType('FORCE_ENABLE')
      setReason('')
    }
  }, [open])

  const submit = async () => {
    if (!providerModelId || !capabilityKey.trim()) {
      message.warning('请填写 Capability Key，例如 param.resolution.1080P')
      return
    }
    setSubmitting(true)
    try {
      await platformSetModelCapabilityOverride(providerModelId, capabilityKey.trim(), {
        override_type: overrideType,
        reason: reason.trim() || undefined,
      })
      message.success('管理员覆盖已保存')
      onSaved()
      onClose()
    } catch (err) {
      message.error(errMsg(err, '保存覆盖失败'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="编辑能力配置（Admin Override）"
      open={open}
      onCancel={onClose}
      onOk={() => void submit()}
      okButtonProps={{ loading: submitting }}
      okText="保存"
      cancelText="取消"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: platformColors.secondaryText }}>
          此处编辑的是 Vistoera Admin Override，不会修改官方能力（Official Capability）。示例 Capability Key：
          <code>param.resolution.1080P</code> / <code>flag.reference_video</code> / <code>input.AUDIO</code>。
        </p>
        <Input
          placeholder="Capability Key，例如 param.resolution.1080P"
          value={capabilityKey}
          onChange={(e) => setCapabilityKey(e.target.value)}
        />
        <Select
          value={overrideType}
          onChange={(v) => setOverrideType(v)}
          options={[
            { value: 'FORCE_ENABLE', label: 'FORCE_ENABLE（强制启用）' },
            { value: 'FORCE_DISABLE', label: 'FORCE_DISABLE（强制禁用）' },
          ]}
        />
        <Input.TextArea
          placeholder="原因（建议填写，例如：Vistoera 实测确认 1080P 可正常生成）"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
        />
      </div>
    </Modal>
  )
}

function ModelDetailDrawer({
  providerModelId,
  onClose,
  onChanged,
}: {
  providerModelId: string | null
  onClose: () => void
  onChanged: () => void
}) {
  const [detail, setDetail] = useState<ModelCapabilityDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [overrideModalOpen, setOverrideModalOpen] = useState(false)

  const load = useCallback(() => {
    if (!providerModelId) return
    setLoading(true)
    platformGetModelCapabilityDetail(providerModelId)
      .then((res) => setDetail(res.data))
      .catch((err) => message.error(errMsg(err, '加载模型能力详情失败')))
      .finally(() => setLoading(false))
  }, [providerModelId])

  useEffect(() => {
    load()
  }, [load])

  const removeOverride = async (capabilityKey: string) => {
    if (!providerModelId) return
    try {
      await platformRemoveModelCapabilityOverride(providerModelId, capabilityKey)
      message.success('已移除覆盖，恢复跟随官方能力')
      load()
      onChanged()
    } catch (err) {
      message.error(errMsg(err, '移除覆盖失败'))
    }
  }

  const syncOne = async () => {
    if (!providerModelId) return
    setSyncing(true)
    try {
      const res = await platformSyncModelCapability(providerModelId)
      if (res.data.synced === false) {
        message.info(res.data.reason === 'NO_ADAPTER' ? '该 Provider 暂无自动同步来源（Manual）' : '暂无新的官方能力数据')
      } else if (res.data.changed) {
        message.success('检测到能力更新，已生成新的 Candidate 快照')
      } else {
        message.success('官方能力无变化')
      }
      load()
      onChanged()
    } catch (err) {
      message.error(errMsg(err, '同步失败'))
    } finally {
      setSyncing(false)
    }
  }

  const activateSnapshot = async (snapshotId: string) => {
    try {
      await platformActivateModelCapabilitySnapshot(snapshotId)
      message.success('已激活该快照')
      load()
      onChanged()
    } catch (err) {
      message.error(errMsg(err, '激活失败'))
    }
  }

  return (
    <Drawer
      title={detail ? `${detail.display_name} 模型能力详情` : '模型能力详情'}
      width={720}
      open={!!providerModelId}
      onClose={onClose}
      destroyOnClose
      extra={
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" icon={<SyncOutlined />} loading={syncing} onClick={() => void syncOne()}>
            同步此模型
          </Button>
          <Button size="small" type="primary" onClick={() => setOverrideModalOpen(true)}>
            编辑能力配置
          </Button>
        </div>
      }
    >
      {loading || !detail ? (
        <Empty description="加载中..." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: platformColors.mutedText }}>{detail.provider_model_external_id}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                <span>{detail.provider_display_name || detail.provider_id}</span>
                <Tag>{detail.model_type}</Tag>
                <Tag color={STATUS_COLOR[detail.capability_status]}>{STATUS_LABEL[detail.capability_status]}</Tag>
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 12, color: platformColors.mutedText }}>
              <div>来源：{detail.has_automatic_source ? '官方 API / 数据' : 'Manual / 暂无自动来源'}</div>
              <div>最近同步：{formatDateTime(detail.official_snapshot?.fetched_at)}</div>
            </div>
          </div>

          <Tabs
            defaultActiveKey="overview"
            items={[
              {
                key: 'overview',
                label: '能力概览',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <SectionCard title="输入能力 (支持)">
                      <ModalityChecklist allKnown={KNOWN_INPUT_MODALITIES} active={detail.effective.input_modalities} />
                    </SectionCard>
                    <SectionCard title="输出能力 (支持)">
                      <ModalityChecklist allKnown={KNOWN_OUTPUT_MODALITIES} active={detail.effective.output_modalities} />
                    </SectionCard>
                  </div>
                ),
              },
              {
                key: 'parameters',
                label: '参数限制',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {Object.keys(detail.effective.parameters).length === 0 && (
                      <Empty description="暂无参数能力数据" />
                    )}
                    {Object.entries(detail.effective.parameters).map(([name, spec]) => {
                      const officialSpec = detail.official.parameters[name]
                      return (
                        <SectionCard key={name} title={name}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                            <div>
                              <span style={{ color: platformColors.mutedText }}>Official: </span>
                              {officialSpec
                                ? officialSpec.kind === 'enum'
                                  ? (officialSpec.values || []).join(' / ')
                                  : `${officialSpec.min ?? '?'} - ${officialSpec.max ?? '?'}`
                                : '—'}
                            </div>
                            <div>
                              <span style={{ color: platformColors.mutedText }}>Effective: </span>
                              {spec.kind === 'enum' ? (spec.values || []).join(' / ') : `${spec.min ?? '?'} - ${spec.max ?? '?'}`}
                            </div>
                          </div>
                        </SectionCard>
                      )
                    })}
                  </div>
                ),
              },
              {
                key: 'combinations',
                label: '支持的组合',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detail.effective.combination_constraints.length === 0 && (
                      <Empty description="暂无组合限制数据（未声明限制视为按单项能力判断）" />
                    )}
                    {detail.effective.combination_constraints.map((c) => (
                      <div
                        key={c.key}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          borderRadius: 6,
                          background: 'var(--pf-surface)',
                          border: `1px solid ${platformColors.border}`,
                          fontSize: 13,
                        }}
                      >
                        <span>{JSON.stringify(c.when)}</span>
                        <Tag color={c.supported ? 'success' : 'error'}>
                          {c.supported ? '支持' : '不支持'}
                          {c.source === 'OVERRIDE' ? '（管理员覆盖）' : ''}
                        </Tag>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: 'overrides',
                label: '管理员覆盖',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detail.overrides.length === 0 && <Empty description="暂无管理员覆盖" />}
                    {detail.overrides.map((o) => (
                      <div
                        key={o.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: `1px solid ${platformColors.border}`,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{o.capability_key}</div>
                          <div style={{ fontSize: 12, color: platformColors.mutedText }}>
                            {o.override_type} · {o.reason || '无备注'} · {formatDateTime(o.updated_at)}
                          </div>
                        </div>
                        <Button size="small" onClick={() => void removeOverride(o.capability_key)}>
                          恢复跟随官方
                        </Button>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: 'history',
                label: '更新历史',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detail.snapshot_history.length === 0 && <Empty description="暂无同步历史" />}
                    {detail.snapshot_history.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: `1px solid ${platformColors.border}`,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>
                            v{s.version} · {s.status}
                            {s.is_destructive_vs_previous ? (
                              <Tag color="error" style={{ marginLeft: 6 }}>
                                DESTRUCTIVE
                              </Tag>
                            ) : null}
                          </div>
                          <div style={{ fontSize: 12, color: platformColors.mutedText }}>
                            {s.source_type} · {formatDateTime(s.fetched_at)}
                          </div>
                          {s.diff && (s.diff.added.length || s.diff.removed.length || s.diff.changed.length) ? (
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              {s.diff.added.map((a) => (
                                <div key={`a-${a}`} style={{ color: platformColors.success }}>
                                  + {a}
                                </div>
                              ))}
                              {s.diff.removed.map((r) => (
                                <div key={`r-${r}`} style={{ color: platformColors.danger }}>
                                  - {r}
                                </div>
                              ))}
                              {s.diff.changed.map((c) => (
                                <div key={`c-${c}`} style={{ color: platformColors.warning }}>
                                  {c}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {s.status === 'CANDIDATE' && (
                          <Button size="small" type="primary" onClick={() => void activateSnapshot(s.id)}>
                            激活
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: 'channels',
                label: '执行渠道',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detail.execution_channels.length === 0 && <Empty description="暂未绑定到任何功能节点" />}
                    {detail.execution_channels.map((c) => (
                      <div
                        key={c.binding_id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: `1px solid ${platformColors.border}`,
                          fontSize: 13,
                        }}
                      >
                        <span>{c.function_node_capability}</span>
                        <span>
                          {c.enabled ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>}
                          {c.is_default ? <Tag color="blue">默认</Tag> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}

      <OverrideEditorModal
        open={overrideModalOpen}
        providerModelId={providerModelId}
        onClose={() => setOverrideModalOpen(false)}
        onSaved={() => {
          load()
          onChanged()
        }}
      />
    </Drawer>
  )
}

export default function ModelCapabilitiesPage() {
  const [rows, setRows] = useState<ModelCapabilitySummaryRow[]>([])
  const [total, setTotal] = useState(0)
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({})
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncingAll, setSyncingAll] = useState(false)
  const [providers, setProviders] = useState<ProviderDefinitionRow[]>([])

  const [category, setCategory] = useState<'ALL' | PrimaryCategory>('ALL')
  const [search, setSearch] = useState('')
  const [providerId, setProviderId] = useState<string | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<CapabilityStatus | undefined>(undefined)
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    platformListModelCapabilities({
      category: category === 'ALL' ? undefined : category,
      search: search.trim() || undefined,
      provider_id: providerId,
      capability_status: statusFilter,
      limit: 200,
    })
      .then((res) => {
        setRows(res.data.items ?? [])
        setTotal(res.data.total ?? 0)
        setCategoryCounts(res.data.category_counts ?? {})
        setLastSyncAt(res.data.last_sync_at ?? null)
      })
      .catch((err) => message.error(errMsg(err, '加载模型能力列表失败')))
      .finally(() => setLoading(false))
  }, [category, search, providerId, statusFilter])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    platformListCredentialProviders()
      .then((res) => setProviders(res.data.providers ?? []))
      .catch(() => undefined)
  }, [])

  const syncAll = async () => {
    setSyncingAll(true)
    try {
      const res = await platformSyncAllModelCapabilities()
      message.success(
        `同步完成：处理 ${res.data.processed} 个模型（${res.data.synced_changed} 个有更新，${res.data.no_adapter} 个无自动来源，${res.data.errors.length} 个失败）`,
      )
      load()
    } catch (err) {
      message.error(errMsg(err, '批量同步失败'))
    } finally {
      setSyncingAll(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: platformColors.primaryText }}>模型能力</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: platformColors.secondaryText, maxWidth: 760 }}>
            管理所有 AI 模型的输入输出能力、参数限制和适用场景。数据来源于官方 API、文档或测试确认。
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button icon={<CloudSyncOutlined />} type="primary" loading={syncingAll} onClick={() => void syncAll()}>
            同步所有模型能力
          </Button>
          <div style={{ fontSize: 12, color: platformColors.mutedText, marginTop: 6 }}>
            上次同步：{formatDateTime(lastSyncAt)}
          </div>
        </div>
      </div>

      <Tabs
        activeKey={category}
        onChange={(k) => setCategory(k as 'ALL' | PrimaryCategory)}
        items={CATEGORY_TABS.map((t) => ({
          key: t.key,
          label: `${t.label} (${categoryCounts[t.key] ?? 0})`,
        }))}
      />

      <SectionCard
        title="模型能力列表"
        extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} size="small" />}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <Input.Search
            placeholder="搜索模型名称 / Provider / 外部 Model ID"
            allowClear
            style={{ width: 320 }}
            onSearch={setSearch}
          />
          <Select
            allowClear
            placeholder="全部 Provider"
            style={{ width: 180 }}
            value={providerId}
            onChange={setProviderId}
            options={providers.map((p) => ({ value: p.id, label: p.display_name }))}
          />
          <Select
            allowClear
            placeholder="全部状态"
            style={{ width: 150 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </div>

        <Table
          rowKey="provider_model_id"
          loading={loading}
          dataSource={rows}
          size="middle"
          scroll={{ x: 1200 }}
          onRow={(row) => ({ onClick: () => setSelectedModelId(row.provider_model_id), style: { cursor: 'pointer' } })}
          pagination={{ total, pageSize: 200, showSizeChanger: false, showTotal: (t) => `共 ${t} 个模型` }}
          columns={[
            {
              title: '模型名称',
              dataIndex: 'display_name',
              render: (name: string, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 11, color: platformColors.mutedText }}>{row.provider_model_external_id}</div>
                </div>
              ),
            },
            {
              title: 'Provider',
              dataIndex: 'provider_display_name',
              width: 140,
              render: (v: string | null, row) => v || row.provider_key,
            },
            {
              title: '分类',
              dataIndex: 'primary_category',
              width: 110,
              render: (cat: PrimaryCategory) => (
                <Tag>{CATEGORY_TABS.find((c) => c.key === cat)?.label || cat}</Tag>
              ),
            },
            {
              title: '输入能力',
              dataIndex: 'input_modalities',
              width: 160,
              render: (modalities: string[]) => <ModalityTags modalities={modalities} />,
            },
            {
              title: '输出能力',
              dataIndex: 'output_modalities',
              width: 160,
              render: (modalities: string[]) => <ModalityTags modalities={modalities} />,
            },
            {
              title: '关键能力',
              dataIndex: 'key_capability_tags',
              width: 220,
              render: (tags: string[]) => (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {tags.map((t) => (
                    <Tag key={t} style={{ margin: 0, fontSize: 11 }}>
                      {t}
                    </Tag>
                  ))}
                </div>
              ),
            },
            {
              title: '状态',
              dataIndex: 'capability_status',
              width: 130,
              render: (status: CapabilityStatus, row) => (
                <span>
                  <Tag color={STATUS_COLOR[status]}>{STATUS_LABEL[status]}</Tag>
                  {row.override_count > 0 ? (
                    <Tooltip title={`${row.override_count} 个管理员覆盖`}>
                      <InfoCircleOutlined style={{ marginLeft: 4, color: platformColors.info }} />
                    </Tooltip>
                  ) : null}
                </span>
              ),
            },
            {
              title: '能力来源',
              dataIndex: 'has_automatic_source',
              width: 120,
              render: (v: boolean) => (v ? '官方 API + 文档' : 'Manual'),
            },
            {
              title: '最后同步',
              dataIndex: 'last_sync_at',
              width: 140,
              render: (v: string | null) => formatDateTime(v),
            },
            {
              title: '操作',
              key: 'actions',
              width: 90,
              fixed: 'right',
              render: (_: unknown, row) => (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedModelId(row.provider_model_id)
                  }}
                >
                  查看详情
                </Button>
              ),
            },
          ]}
          locale={{ emptyText: <Empty description="暂无模型" /> }}
        />
      </SectionCard>

      <ModelDetailDrawer providerModelId={selectedModelId} onClose={() => setSelectedModelId(null)} onChanged={load} />
    </div>
  )
}
